import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, ShieldCheck } from "lucide-react";
import { MaskLine } from "./motion/Reveal";
import { IMAGES } from "../data/content";
import { RATING } from "../data/social";
import { useLowestPrice } from "../lib/useApi";
import { formatPrice } from "../lib/filters";
import { PRICE_FROM } from "../data/social";

const TRUST_STRIP = [
  "Wall Mounted & Floor Mounted",
  "Size Guidance Before Order",
  "Transparent Dispatch Timelines",
  "Jaipur Installation Support",
];

export const Hero = () => {
  const ref = useRef(null);
  const { data: fromPrice } = useLowestPrice();
  const lowest = Number.isFinite(fromPrice) ? formatPrice(fromPrice) : PRICE_FROM;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-24 md:pt-32 pb-10 overflow-hidden"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        {/* Copy */}
        <div className="lg:col-span-5 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="eyebrow text-taupe mb-6 flex items-center gap-3"
          >
            <span className="h-px w-8 bg-taupe inline-block" />
            Bathroom Vanities · India
          </motion.p>

          <h1 className="font-serif font-light text-charcoal text-[3rem] sm:text-6xl lg:text-[4.6rem] leading-[0.98] tracking-tight">
            <MaskLine delay={0.35}>Premium</MaskLine>
            <MaskLine delay={0.45}>Bathroom Vanities,</MaskLine>
            <MaskLine delay={0.55} className="italic text-taupe">Chosen with</MaskLine>
            <MaskLine delay={0.65} className="italic text-taupe">Confidence.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-6 md:mt-8 max-w-md text-charcoal/70 text-[0.95rem] md:text-lg leading-relaxed"
          >
            Curated wall mounted and floor mounted vanities — with size
            guidance before you order, transparent dispatch timelines, and
            installation support across Jaipur.
          </motion.p>

          {/* Micro-trust: rating */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            data-testid="hero-rating"
            className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1.5"
          >
            <span className="inline-flex items-center gap-0.5" aria-label={`${RATING.score} star rating`}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={15} strokeWidth={0} className="fill-charcoal" />
              ))}
            </span>
            <span className="text-sm font-medium text-charcoal">{RATING.score}/5</span>
            <span className="text-sm text-charcoal/60">· {RATING.count}+ bathrooms delivered</span>
          </motion.div>

          {/* CTAs + price anchor */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 flex flex-col gap-5"
          >
            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
              <Link
                to="/vanities"
                data-testid="hero-shop-cta"
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-charcoal px-7 py-4 text-sm font-medium text-ivory btn-fill hover:text-charcoal transition-colors duration-500 w-full sm:w-auto"
              >
                Shop Vanities
                <ArrowRight
                  size={17}
                  strokeWidth={1.5}
                  className="transition-transform duration-500 group-hover:translate-x-1"
                />
              </Link>
              <p data-testid="hero-price-anchor" className="text-sm text-charcoal/70">
                From{" "}
                <span className="font-serif text-2xl text-charcoal align-middle">{lowest}</span>
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#categories"
                data-testid="hero-explore-cta"
                className="link-sweep inline-flex items-center gap-2 text-sm font-medium text-charcoal"
              >
                Explore Categories
              </a>
            </div>

            {/* Size-worry killer */}
            <div
              data-testid="hero-size-assurance"
              className="inline-flex items-center gap-2.5 w-fit rounded-full bg-stone/70 border border-beige px-4 py-2.5"
            >
              <ShieldCheck size={16} strokeWidth={1.6} className="text-charcoal shrink-0" />
              <span className="text-xs sm:text-sm text-charcoal/80">
                We confirm your size before dispatch — <span className="font-medium text-charcoal">free</span>.
              </span>
            </div>
          </motion.div>
        </div>

        {/* Image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 relative"
        >
          <div className="relative aspect-[4/5] sm:aspect-[16/12] lg:aspect-[4/4.4] overflow-hidden rounded-sm spotlight">
            <motion.img
              src={IMAGES.hero}
              alt="Premium modern bathroom vanity"
              fetchPriority="high"
              decoding="async"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/25 via-transparent to-transparent" />
          </div>

          {/* Floating caption card */}
          <motion.div
            style={{ y: overlayY }}
            className="absolute -bottom-6 left-4 md:left-8 bg-ivory/95 backdrop-blur-sm border border-beige px-6 py-4 rounded-sm max-w-[240px]"
          >
            <p className="eyebrow text-taupe">Curated for you</p>
            <p className="font-serif text-xl text-charcoal mt-1 leading-tight">
              Two collections. One confident choice.
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* Trust strip */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.9 }}
        data-testid="hero-trust-strip"
        className="max-w-[1600px] mx-auto px-6 md:px-12 mt-10 lg:mt-20"
      >
        <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-beige">
          {TRUST_STRIP.map((t, i) => (
            <div
              key={t}
              className={`py-5 lg:py-6 px-2 lg:px-6 flex items-start gap-3 ${
                i !== 0 ? "lg:border-l border-beige" : ""
              }`}
            >
              <span className="font-serif text-taupe text-lg leading-none">
                0{i + 1}
              </span>
              <span className="text-[0.82rem] md:text-sm text-charcoal/80 leading-snug">
                {t}
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
